import { formatAddress } from "@medplum/core";
import { HealthcareService, Location, Organization } from "@medplum/fhirtypes";

// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import dayjs from "dayjs";

import { Extensions } from "@/fhir/enums/extensions";
import { LONG_DATE_WITH_WEEKDAY_DAYJS_FORMAT } from "@/scheduling/constants/date-formats";
import { getAppointmentTypeCharacteristicValue } from "@/scheduling/utils/scheduling";

import { ACUITY_DEFAULT_TIMEZONE } from "./constants";
import type { ClassEventDetails } from "./types";

export const DEFAULT_CLASS_EVENT_COLOR = "#B7A6EB";

const EMPTY_CLASS_EVENT_DETAILS: ClassEventDetails = {
  title: "",
  category: "",
  organization: "",
  address: "",
  color: DEFAULT_CLASS_EVENT_COLOR,
  instructors: [],
};

/**
 * Builds display details for a class event from appointment type, location, and organization.
 *
 * @param appointmentType - HealthcareService representing the class appointment type.
 * @param location - Location where the class is held.
 * @param organization - Managing organization (practice).
 * @returns Populated details, or an empty fallback when any input is missing.
 */
export function buildClassEventDetails(
  appointmentType: HealthcareService | undefined,
  location: Location | undefined,
  organization: Organization | undefined,
): ClassEventDetails {
  if (!appointmentType || !location || !organization) {
    return EMPTY_CLASS_EVENT_DETAILS;
  }

  const color = getAppointmentTypeCharacteristicValue(
    appointmentType,
    "appointment-type-color",
    Extensions.AcuityAppointmentTypeColor,
  );

  return {
    title: location.name ?? "",
    category: appointmentType.name ?? "",
    organization: organization.name ?? "",
    address: formatAddress(location.address),
    instructors: [],
    color: color ?? DEFAULT_CLASS_EVENT_COLOR,
  };
}

/**
 * Returns the configured duration in minutes for a class appointment type.
 *
 * @param appointmentType - HealthcareService representing the class appointment type.
 * @returns Duration in minutes, or NaN when the characteristic is missing or invalid.
 */
export function getAppointmentTypeDuration(appointmentType: HealthcareService | undefined): number {
  return Number(
    getAppointmentTypeCharacteristicValue(
      appointmentType,
      "appointment-type-duration",
      Extensions.AcuityAppointmentTypeDuration,
    ),
  );
}

/**
 * Builds the public class enrollment route path.
 *
 * @param practiceId - Organization (practice) ID.
 * @param scheduleId - Schedule ID.
 * @param appointmentTypeId - HealthcareService (class type) ID.
 * @returns Absolute path segment for the public enroll page.
 */
export function buildClassEnrollUrl(
  practiceId: string,
  scheduleId: string,
  appointmentTypeId: string,
): string {
  return `/schedule/class/${practiceId}/calendar/${scheduleId}/class/${appointmentTypeId}/enroll`;
}

export const formatTime = (datetime: string): string => {
  return dayjs(datetime).tz(ACUITY_DEFAULT_TIMEZONE).format("h:mm A");
};

export const formatDate = (datetime: string): string => {
  return dayjs(datetime).tz(ACUITY_DEFAULT_TIMEZONE).format(LONG_DATE_WITH_WEEKDAY_DAYJS_FORMAT);
};

export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = dayjs(startDate).tz(ACUITY_DEFAULT_TIMEZONE).format("MMMM D, YYYY");
  const end = dayjs(endDate).tz(ACUITY_DEFAULT_TIMEZONE).format("MMMM D, YYYY");

  if (start === end) {
    return start;
  }

  return `${start} - ${end}`;
};

export const calculateVisibleRange = (
  currentStart: number,
  currentEnd: number,
  threshold: number,
  totalItems: number,
  direction: "next" | "previous",
): { start: number; end: number } => {
  if (direction === "next") {
    const nextStart = Math.min(currentStart + threshold, totalItems - threshold);
    const nextEnd = Math.min(currentEnd + threshold, totalItems);
    return { start: nextStart, end: nextEnd };
  } else {
    const nextStart = Math.max(currentStart - threshold, 0);
    const nextEnd = Math.max(currentEnd - threshold, threshold);
    return { start: nextStart, end: nextEnd };
  }
};
